/**
 * Таблица тинтов для мешера — замена `require('minecraft-data')('1.16.2').tints`.
 *
 * Из всего minecraft-data вендоренному генератору геометрии
 * (`mesher/vendor/section-geometry.js`) нужны только тинты: цвета травы,
 * листвы, воды и редстоуна. Тащить в браузер сам пакет нельзя — официальная
 * web-сборка prismarine-viewer весит 63 МБ ровно из-за него.
 *
 * Значения — усреднённые цвета ванильных биомов. Точную таблицу minecraft-data
 * сознательно не копируем: она под чужой лицензией, а на глаз разница между
 * «травой равнин» и «травой саванны» в камере бота не различима.
 *
 * Формат — как в minecraft-data: `{ default, data: [{ keys, color }] }`;
 * генератор сам превращает его в `Map` с дефолтом через Proxy.
 */

export interface TintTable {
  default: number
  data: Array<{ keys: string[]; color: number }>
}

export interface Tints {
  grass: TintTable
  foliage: TintTable
  water: TintTable
  redstone: TintTable
  constant: TintTable
}

const GRASS: TintTable = {
  default: 0x91bd59,
  data: [
    { keys: ['swamp', 'mangrove_swamp'], color: 0x6a7039 },
    { keys: ['desert', 'savanna', 'savanna_plateau', 'windswept_savanna'], color: 0xbfb755 },
    { keys: ['badlands', 'eroded_badlands', 'wooded_badlands'], color: 0x90814d },
    { keys: ['jungle', 'sparse_jungle', 'bamboo_jungle'], color: 0x59c93c },
    {
      keys: [
        'taiga',
        'old_growth_pine_taiga',
        'old_growth_spruce_taiga',
        'snowy_taiga',
        'grove',
        'windswept_forest'
      ],
      color: 0x86b783
    },
    {
      keys: ['snowy_plains', 'snowy_beach', 'snowy_slopes', 'ice_spikes', 'frozen_peaks'],
      color: 0x80b497
    },
    { keys: ['dark_forest', 'pale_garden'], color: 0x507a32 }
  ]
}

const FOLIAGE: TintTable = {
  default: 0x77ab2f,
  data: [
    { keys: ['swamp', 'mangrove_swamp'], color: 0x6a7039 },
    { keys: ['jungle', 'sparse_jungle', 'bamboo_jungle'], color: 0x30bb0b },
    { keys: ['badlands', 'eroded_badlands', 'wooded_badlands'], color: 0x9e814d },
    {
      keys: ['taiga', 'old_growth_pine_taiga', 'old_growth_spruce_taiga', 'snowy_taiga', 'grove'],
      color: 0x68a464
    },
    { keys: ['snowy_plains', 'snowy_slopes', 'ice_spikes', 'frozen_peaks'], color: 0x60a17b }
  ]
}

const WATER: TintTable = {
  default: 0x3f76e4,
  data: [
    { keys: ['swamp', 'mangrove_swamp'], color: 0x617b64 },
    { keys: ['warm_ocean', 'lukewarm_ocean', 'deep_lukewarm_ocean'], color: 0x45adf2 },
    { keys: ['cold_ocean', 'deep_cold_ocean'], color: 0x3d57d6 },
    { keys: ['frozen_ocean', 'deep_frozen_ocean', 'frozen_river'], color: 0x3938c9 },
    { keys: ['cherry_grove'], color: 0x5db7ef }
  ]
}

/** Редстоун: яркость по уровню сигнала 0..15. */
const REDSTONE: TintTable = {
  default: 0x8f0000,
  data: Array.from({ length: 16 }, (_unused, power) => ({
    keys: [String(power)],
    color: Math.round(255 * (0.3 + (power / 15) * 0.7)) << 16
  }))
}

/** Блоки с фиксированным тинтом (не зависящим от биома). */
const CONSTANT: TintTable = {
  default: 0xffffff,
  data: [
    { keys: ['birch_leaves'], color: 0x80a755 },
    { keys: ['spruce_leaves'], color: 0x619961 },
    { keys: ['lily_pad'], color: 0x208030 }
  ]
}

export const tints: Tints = {
  grass: GRASS,
  foliage: FOLIAGE,
  water: WATER,
  redstone: REDSTONE,
  constant: CONSTANT
}
